import React, { createContext, useContext, useEffect, useState } from 'react';

import useQueryString from '../../hooks/ui-control/useQueryString';

interface TableGroupProps {
  children: React.ReactNode[];
  titles: string[];
  defaultActiveIndex?: number;
}

const TableGroup: React.FC<TableGroupProps> = ({
  children,
  titles,
  defaultActiveIndex,
}) => {
  return (
    <TableGroupProvider titles={titles} defaultActiveIndex={defaultActiveIndex}>
      <TableGroupContent titles={titles}>{children}</TableGroupContent>
    </TableGroupProvider>
  );
};

const TableGroupContent: React.FC<TableGroupProps> = ({ children, titles }) => {
  const tableGroupContext = useContext(TableGroupContext);

  if (!tableGroupContext) {
    return null;
  }

  const activeIndex = titles.findIndex(
    (title) => title === tableGroupContext.queryParams.get('tab-segment'),
  );

  if (activeIndex >= 0 && activeIndex < children.length) {
    return <React.Fragment>{children[activeIndex]}</React.Fragment>;
  }

  return null;
};

export default TableGroup;

interface ITableGroupProvider {
  children: React.ReactNode;
  titles: string[];
  defaultActiveIndex?: number;
}

interface ITableGroupContext {
  groupTitles: string[];
  setGroupTitles: (groupTitles: string[]) => void;
  queryParams: URLSearchParams;
  setQueryParams: (params: Record<string, string>) => void;
}

export const TableGroupContext = createContext<ITableGroupContext | null>(null);

export const TableGroupProvider: React.FC<ITableGroupProvider> = ({
  children,
  titles,
  defaultActiveIndex,
}) => {
  const [queryParams, setQueryParams] = useQueryString();
  const [groupTitles, setGroupTitles] = useState<string[]>(titles);

  useEffect(() => {
    if (titles.length > 0) {
      setQueryParams({
        'tab-segment':
          queryParams.get('tab-segment') || titles[defaultActiveIndex || 0],
      });
    }

    // eslint-disable-next-line
  }, [titles]);

  return (
    <TableGroupContext.Provider
      value={{
        groupTitles,
        setGroupTitles,
        queryParams,
        setQueryParams,
      }}
    >
      {children}
    </TableGroupContext.Provider>
  );
};
